import classNames from "classnames";
import { useCallback, useRef, useState } from "react";
import arrowIcon from "../../assets/down-arrow.svg";
import { DropdownNode } from "../../build/types";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import DropdownOption from "../BuilderDropdownField/DropdownOption";
import { DropdownProps } from "../BuilderInterface";
import { getCompoundStyle, getStateStyleForBuilder, getStylesFromInteractiveStates } from "../helpers";
import "./builder-tax-configuration.scss";
import { useInteractionStates } from "../../hooks/useInteractionStates";

const BuilderTaxConfiguration = ({ node, builderProps, dropdownOptions, selectedOptionLabel, renderProps }: DropdownProps) => {
    const dNode = node as DropdownNode;
    const { id, attr, input, optionWrapperStyle, stateManager, previewOptions } = dNode;
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const { setElement, states } = useInteractionStates<HTMLLabelElement>();

    const dropdownRef = useRef<HTMLLabelElement | null>(null);

    const combinedRef = useCallback(
        (el: HTMLLabelElement) => {
            setElement(el);
            if (dropdownRef) {
                dropdownRef.current = el;
            }
        },
        [dropdownRef]
    );

    useOutsideClick(dropdownRef, () => {
        if (!builderProps) {
            setDropdownOpen(false);
        }
    });

    const handleOptionClick = (value: string) => {
        if (value && renderProps?.callback) {
            renderProps.callback(value);
            setDropdownOpen(false);
        }
    };

    const dropdownFieldClass = classNames("builder-dropdown-field", {
        [`${attr?.className}`]: !!attr?.className
    });

    const inputStyle = input?.style;
    const labelStyle = input?.labelStyle;
    const toggleStyle = input?.toggleStyle;
    const toggleColor = toggleStyle?.backgroundColor;


    if (builderProps) {
        const { selectedMenu, nodeState, selectCallback, outlineCallback } = builderProps;
        return (
            <label
                {...attr}
                className={dropdownFieldClass}
                style={getCompoundStyle({
                    node,
                    otherStyle: inputStyle,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                })}
                id={id}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                ref={dropdownRef}
            >
                <div className="tax-config-header">
                    <div className="tax-label" style={labelStyle}>
                        {input?.label || "Select Tax"}
                    </div>
                    <div className="toggle-wrapper" style={toggleStyle}>
                        <input type="checkbox" checked={true} readOnly />
                        <div className="custom-toggle-input" style={{ backgroundColor: toggleColor }}>
                            <div className="toggle-handle" />
                        </div>
                    </div>
                </div>

                <div className="input-wrapper">
                    <input
                        type="text"
                        value={selectedOptionLabel || ""}
                        placeholder={input?.placeholder}
                        readOnly
                        className={dropdownOpen ? "dropdown-open" : ""}
                        style={getCompoundStyle({
                            otherStyle: inputStyle,
                            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "input_style")
                        })}
                    />
                    <img src={arrowIcon} className={dropdownOpen ? "invert-image" : ""} />
                    {previewOptions && (
                        <div
                            className="dropdown-field-option-wrapper"
                            style={getCompoundStyle({
                                otherStyle: optionWrapperStyle,
                                stateStyle: getStateStyleForBuilder(stateManager, nodeState, "optionWrapperStyle")
                            })}
                        >
                            {dropdownOptions &&
                                Object.entries(dropdownOptions).map(([value, label], index) => (
                                    <DropdownOption
                                        node={node}
                                        key={index}
                                        label={label}
                                        value={value}
                                        builder
                                        nodeState={nodeState}
                                    />
                                ))}
                        </div>
                    )}
                </div>

                {selectedMenu}
            </label>
        );
    } else {
        const labelStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({
                otherStyle: inputStyle,
                node,
            }),
            stateManager,
            paths: ["attr_style"]
        });

        const optionStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: optionWrapperStyle,
            stateManager,
            paths: ["optionWrapperStyle"]
        });

        return (
            <label {...attr} className={dropdownFieldClass} style={labelStyle} id={id} ref={combinedRef}>
                <div className="tax-config-header">
                    <div className="tax-label" style={labelStyle}>
                        {input?.label || "Select Tax"}
                    </div>
                    <div className="toggle-wrapper" style={toggleStyle}>
                        <input type="checkbox" checked={renderProps?.checked} />
                        <div className="custom-toggle-input" style={{ backgroundColor: toggleColor }} onClick={renderProps?.toggle}>
                            <div className="toggle-handle" />
                        </div>
                    </div>
                </div>

                <div className="input-wrapper">
                    <input
                        type="text"
                        value={selectedOptionLabel || ""}
                        placeholder={input?.placeholder}
                        readOnly
                        className={dropdownOpen ? "dropdown-open" : ""}
                        style={labelStyle}
                        onClick={renderProps?.checked ? () => setDropdownOpen(prev => !prev) : undefined}
                        disabled={!renderProps?.checked}
                    />
                    <img
                        src={arrowIcon}
                        className={dropdownOpen ? "invert-image" : ""}
                        onClick={renderProps?.checked ? () => setDropdownOpen(prev => !prev) : undefined}
                    />
                    {dropdownOpen && (
                        <div className="dropdown-field-option-wrapper" style={optionStyle}>
                            {dropdownOptions &&
                                Object.entries(dropdownOptions).map(([value, label], index) => (
                                    <DropdownOption
                                        node={node}
                                        key={index}
                                        label={label}
                                        value={value}
                                        callback={() => handleOptionClick(value)}
                                    />
                                ))}
                        </div>
                    )}
                </div>
            </label>
        );
    }
};

export default BuilderTaxConfiguration;
